/**
 * Why Edit job cannot be saved right now: the job's queued or running tasks.
 *
 * The backend names them - `stats.in_flight_tasks` on a job, numbered the way
 * the job's rows are - and refuses a change to the job's configuration while
 * any of them is in flight, with the same names in its message.
 */

export type InFlightTask = {
  id: number;
  /** The task's number on the panel row; null for the roster task (R). */
  index_in_job: number | null;
  roster_only: boolean;
  task_status: string;
};

const GENERIC_REASON =
  "This task job has running tasks. Stop them before editing.";

export function parseInFlightTasks(raw: unknown): InFlightTask[] {
  if (!Array.isArray(raw)) return [];
  const tasks: InFlightTask[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    const id = Number(o.id);
    if (!Number.isInteger(id)) continue;
    const index = o.index_in_job == null ? NaN : Number(o.index_in_job);
    tasks.push({
      id,
      index_in_job: Number.isInteger(index) ? index : null,
      roster_only: o.roster_only === true,
      task_status: String(o.task_status ?? "").toUpperCase(),
    });
  }
  return tasks;
}

function taskName(task: InFlightTask): string {
  if (task.roster_only) return "the roster task (R)";
  return task.index_in_job != null
    ? `Task ${task.index_in_job}`
    : `Task #${task.id}`;
}

/** The Edit job warning: names the blocking tasks when the backend listed them. */
export function editJobLockedReason(tasks: InFlightTask[]): string {
  if (!tasks.length) return GENERIC_REASON;
  const names = tasks.map(taskName);
  const one = names.length === 1;
  const list = one
    ? names[0]
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  const sentence =
    `${list} ${one ? "is" : "are"} queued or running - stop ` +
    `${one ? "it" : "them"} before editing this job.`;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}
